import { EmptyState, ErrorBoundary, FlexBox } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import type {
  ContextControl,
  InstrumentPanel as InstrumentPanelT,
  PeriodControl,
  ReadInstrumentsQueryFn,
  SettingsControl,
} from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

import { AnalyticsContext } from 'analytics/components/controls/components/context/analyticsContext';
import { AnalyticsPeriod } from 'analytics/components/controls/components/period/analyticsPeriod';
import { InstrumentPanel } from 'analytics/components/panels/components/panel/components/panel/instrumentPanel';
import { InstrumentRenderer } from 'analytics/components/panels/components/panel/components/renderer/instrumentRenderer';
import { panelGridLayoutConfig } from 'analytics/components/panels/utils/panelGridLayoutConfig';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';

import { usePersistInstrumentSettings } from '../../../../utils/usePersistInstrumentSettings';
import { InstrumentRendererError } from '../../../renderer/components/error/instrumentRendererError';
import {
  InstrumentComparateSide,
  InstrumentComparate as InstrumentComparateT,
} from '../../instrumentComparison.types';

import styles from './instrumentComparate.module.scss';

type InstrumentComparateProps = {
  /** The instrument panel. */
  panel: InstrumentPanelT;
  /** The side of the comparison. */
  side: InstrumentComparateSide;
  /** The comparate state. */
  comparate: InstrumentComparateT;
  /** Callback to update a comparate. */
  onUpdate: (
    side: InstrumentComparateSide,
    comparate: InstrumentComparateT,
  ) => void;
};

export const InstrumentComparate = memo(function InstrumentComparate({
  panel,
  side,
  comparate: { controls },
  onUpdate,
}: InstrumentComparateProps): JSX.Element {
  const { dashboard, filters } = useAnalyticsState();

  const [
    readInstrumentsQuery,
    { instruments, isLoading, isFetching, isError },
  ] = analyticsApi.useLazyReadComparisonInstrumentsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      instruments: data?.data.instruments ?? [],
    }),
  });

  const { instrumentSettings, setInstrumentSettings } =
    usePersistInstrumentSettings();

  /** Fetches the list of instruments for the panel. */
  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const response = await readInstrumentsQuery({
        side,
        payload: {
          id: panel.id,
          context: controls.context,
          period: controls.period,
          filters,
          settings: {
            ...controls.settings,
            ...instrumentSettings,
          },
          meta: { ...arg?.meta },
        },
      });

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [
      controls,
      filters,
      instrumentSettings,
      panel.id,
      readInstrumentsQuery,
      side,
    ],
  );

  // Preloads the data on mount for the left side of the comparison.
  useMount(() => {
    if (side === 'left') readInstruments();
  });

  // Updates the instrument data when the comparate controls or the instrument settings change.
  useUpdateEffect(() => {
    readInstruments();
  }, [controls, instrumentSettings]);

  const handleChangeContext = useCallback(
    (context: ContextControl): void => {
      onUpdate(side, { controls: { ...controls, context } });

      ampli.analyticsCompareContextUpdated({
        dashboard: dashboard.title,
        panel: panel.title,
        side,
      });
    },
    [controls, dashboard.title, onUpdate, panel.title, side],
  );

  const handleChangePeriod = useCallback(
    (period: PeriodControl): void => {
      onUpdate(side, { controls: { ...controls, period } });

      ampli.analyticsComparePeriodUpdated({
        dashboard: dashboard.title,
        panel: panel.title,
        side,
        value:
          period.values.length === 1
            ? period.values[0].value
            : 'dateWithDefaultTime',
        timezone: controls.settings.timezone,
      });
    },
    [controls, dashboard.title, onUpdate, panel.title, side],
  );

  const handleChangeSettings = useCallback(
    (newSettings: SettingsControl): void => {
      onUpdate(side, {
        controls: {
          ...controls,
          settings: {
            ...controls.settings,
            ...newSettings,
          },
        },
      });

      ampli.analyticsComparePeriodUpdated({
        dashboard: dashboard.title,
        panel: panel.title,
        side,
        timezone: newSettings.timezone,
        value:
          controls.period.values.length === 1
            ? controls.period.values[0].value
            : 'dateWithDefaultTime',
        withSchedule: !!newSettings.schedule_id,
      });
    },
    [controls, dashboard.title, onUpdate, panel.title, side],
  );

  /**
   * The panel height is calculated using the same method as react-grid-layout.
   * The height of the panel header, which is not shown in comparisons, and the
   * gap between it and the content below is subtracted from the calculated
   * height, so that the panel is only as tall as the content
   */
  const panelHeight = useMemo(() => {
    const panelHeaderHeight = parseInt(sizes.spacer[500]);
    const panelGap = parseInt(sizes.spacer[400]);

    return (
      Math.round(
        panelGridLayoutConfig.rowHeight * panel.layout.h +
          Math.max(0, panel.layout.h - 1) * panelGridLayoutConfig.margin[1],
      ) -
      panelHeaderHeight -
      panelGap
    );
  }, [panel.layout.h]);

  const isEmpty =
    instruments.length === 0 && !isLoading && !isFetching && !isError;

  return (
    <div className={styles.root}>
      <FlexBox gap="200">
        {dashboard.context && (
          <AnalyticsContext
            context={dashboard.context}
            control={controls.context}
            onChange={handleChangeContext}
          />
        )}

        <AnalyticsPeriod
          period={controls.period}
          settings={controls.settings}
          onChangePeriod={handleChangePeriod}
          onChangeSettings={handleChangeSettings}
        />
      </FlexBox>

      <InstrumentPanel panel={panel} showHeader={false} height={panelHeight}>
        {isEmpty ? (
          <FlexBox flex="1" justifyContent="center" alignItems="center">
            <EmptyState
              title="Nothing to compare"
              description="Select something above to see the comparison."
              icon="exchange"
            />
          </FlexBox>
        ) : (
          <ErrorBoundary fallback={<InstrumentRendererError />}>
            <InstrumentRenderer
              panel={panel}
              instruments={instruments}
              isFetching={isFetching}
              isError={isError}
              onReadInstruments={readInstruments}
              onSetInstrumentSettings={setInstrumentSettings}
            />
          </ErrorBoundary>
        )}
      </InstrumentPanel>
    </div>
  );
});
